import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { gamesAPI, geocodeAPI, messagesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle,
    DialogDescription 
} from '../components/ui/dialog';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
    MapPin, Plus, Users, Clock, Calendar as CalendarIcon, 
    LogOut, User, Sword, Sparkles, Store, X, RefreshCw,
    MessageSquare, Send, Navigation, History, CalendarDays, Bell,
    Filter, Search, SlidersHorizontal, ChevronDown, ChevronUp
} from 'lucide-react';
import notificationService from '../lib/notifications';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color, type) => {
    const svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
            <defs>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </defs>
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z" 
                  fill="${color}" stroke="#00ff41" stroke-width="1" filter="url(#glow)"/>
            <circle cx="12" cy="12" r="6" fill="#050505" stroke="#00ff41" stroke-width="1"/>
            <text x="12" y="16" text-anchor="middle" fill="#00ff41" font-size="8">${type}</text>
        </svg>
    `;
    
    return L.divIcon({
        html: svgIcon,
        className: 'custom-marker',
        iconSize: [32, 48],
        iconAnchor: [16, 48],
        popupAnchor: [0, -48],
    });
};

const gameIcons = {
    mtg: createCustomIcon('#1a1a2e', 'M'),
    dnd: createCustomIcon('#2d132c', 'D'),
    board_game: createCustomIcon('#0c3c26', 'B'),
    lgs: createCustomIcon('#003b00', 'S'),
};

// Location picker component
const LocationPicker = ({ onLocationSelect, selectedLocation }) => {
    useMapEvents({
        click: (e) => {
            onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });

    return selectedLocation ? (
        <Marker position={[selectedLocation.lat, selectedLocation.lng]}>
            <Popup>Selected Location</Popup>
        </Marker>
    ) : null;
};

// Map center updater
const MapCenterUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);
    return null;
};

// Address Autocomplete Component
const AddressAutocomplete = ({ value, onChange, onSelect, placeholder }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);

    const searchAddresses = async (query) => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        try {
            const response = await geocodeAPI.searchAddresses(query);
            setSuggestions(response.data || []);
        } catch (error) {
            console.error('Geocoding error:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        onChange(newValue);

        // Debounce the search
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            searchAddresses(newValue);
        }, 300);
    };

    const handleSelect = (suggestion) => {
        onChange(suggestion.display_name);
        onSelect({
            address: suggestion.display_name,
            lat: suggestion.lat,
            lng: suggestion.lon,
        });
        setShowSuggestions(false);
        setSuggestions([]);
    };

    return (
        <div className="relative">
            <Input
                data-testid="address-autocomplete-input"
                value={value}
                onChange={handleInputChange}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={placeholder}
                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
            />
            {loading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-[#00ff41] border-t-transparent rounded-full animate-spin" />
                </div>
            )}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-[#003b00] max-h-48 overflow-y-auto">
                    {suggestions.map((suggestion) => (
                        <div
                            key={suggestion.place_id || suggestion.display_name}
                            onClick={() => handleSelect(suggestion)}
                            className="px-3 py-2 cursor-pointer hover:bg-[#003b00] text-[#00ff41] text-sm font-mono border-b border-[#003b00] last:border-b-0"
                        >
                            {suggestion.display_name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Game Chat Component
const GameChat = ({ gameId, currentUser, isParticipant }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);
    const { subscribe } = useWebSocket();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Fetch messages and mark as read
    useEffect(() => {
        const fetchMessages = async () => {
            if (!isParticipant) return;
            
            try {
                setLoading(true);
                const response = await messagesAPI.getMessages(gameId);
                setMessages(response.data || []);
                
                // Mark messages as read
                try {
                    await messagesAPI.markAsRead(gameId);
                } catch (e) {
                    console.error('Error marking messages as read:', e);
                }
            } catch (error) {
                console.error('Error fetching messages:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [gameId, isParticipant]);

    // Subscribe to new messages
    useEffect(() => {
        if (!isParticipant) return;

        const unsubscribe = subscribe(`chat_${gameId}`, (data) => {
            if (data.type === 'new_message' && data.game_id === gameId) {
                setMessages(prev => [...prev, data.message]);
            }
        });

        return unsubscribe;
    }, [gameId, subscribe, isParticipant]);

    // Scroll to bottom on new messages
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!newMessage.trim() || sending) return;

        try {
            setSending(true);
            const response = await messagesAPI.sendMessage(gameId, newMessage.trim());
            setMessages(prev => [...prev, response.data]);
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isParticipant) {
        return (
            <div className="bg-[#0a0a0a] border border-[#003b00] p-4 text-center">
                <MessageSquare className="w-8 h-8 text-[#003b00] mx-auto mb-2" />
                <p className="text-[#008f11] text-sm font-mono">
                    Join the game to access chat
                </p>
            </div>
        );
    }

    return (
        <div className="bg-[#0a0a0a] border border-[#003b00] flex flex-col h-64">
            <div className="px-3 py-2 border-b border-[#003b00] flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#00ff41]" />
                <span className="text-sm text-[#00ff41] font-mono">GAME_CHAT</span>
            </div>

            <ScrollArea className="flex-1 p-3">
                {loading ? (
                    <div className="flex justify-center py-4">
                        <div className="matrix-loader w-6 h-6" />
                    </div>
                ) : messages.length === 0 ? (
                    <p className="text-[#008f11] text-xs text-center font-mono">
                        No messages yet. Start the conversation!
                    </p>
                ) : (
                    <div className="space-y-3">
                        {messages.map((msg) => (
                            <div 
                                key={msg.message_id}
                                className={`flex gap-2 ${msg.user_id === currentUser?.user_id ? 'flex-row-reverse' : ''}`}
                            >
                                <div className="w-6 h-6 rounded-full overflow-hidden border border-[#003b00] flex-shrink-0">
                                    {msg.user_picture ? (
                                        <img 
                                            src={msg.user_picture} 
                                            alt={msg.user_name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-[#003b00] flex items-center justify-center">
                                            <span className="text-[#00ff41] text-xs">
                                                {msg.user_name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className={`flex-1 ${msg.user_id === currentUser?.user_id ? 'text-right' : ''}`}>
                                    <p className="text-[#008f11] text-xs font-mono">{msg.user_name}</p>
                                    <p className={`text-sm text-[#00ff41] p-2 inline-block ${
                                        msg.user_id === currentUser?.user_id 
                                            ? 'bg-[#003b00]' 
                                            : 'bg-[#050505] border border-[#003b00]'
                                    }`}>
                                        {msg.content}
                                    </p>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </ScrollArea>

            <div className="p-2 border-t border-[#003b00] flex gap-2">
                <Input
                    data-testid="chat-input"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="bg-[#050505] border-[#003b00] text-[#00ff41] text-sm flex-1"
                />
                <Button
                    data-testid="send-message-btn"
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sending}
                    size="sm"
                    className="bg-[#00ff41] text-black px-3"
                >
                    <Send className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

const Dashboard = () => {
    const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
    const { connect, subscribe, connected } = useWebSocket();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGame, setSelectedGame] = useState(null);
    const [showHostModal, setShowHostModal] = useState(false);
    const [showGameModal, setShowGameModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [userLocation, setUserLocation] = useState({ lat: 40.7128, lng: -74.0060 }); // Default NYC
    const [filterType, setFilterType] = useState('all');
    const [searchRadius, setSearchRadius] = useState(50);
    
    // Advanced filter state
    const [advancedFilters, setAdvancedFilters] = useState({
        minPlayers: '',
        maxPlayersLimit: '',
        startDate: null,
        endDate: null,
        hasOpenSlots: false,
        lookingForDm: false,
        searchQuery: '',
    });
    
    // Host form state
    const [hostForm, setHostForm] = useState({
        game_type: 'mtg',
        game_name: '',
        title: '',
        description: '',
        latitude: null,
        longitude: null,
        address: '',
        date_time: new Date(),
        duration_hours: 3,
        max_players: 4,
        looking_for_dm: false,
    });
    const [pickingLocation, setPickingLocation] = useState(false);

    // Define currentUser early so it can be used in useEffect hooks
    const currentUser = user || location.state?.user;

    // Check authentication
    useEffect(() => {
        if (!authLoading && !isAuthenticated && !location.state?.user) {
            navigate('/');
        }
    }, [authLoading, isAuthenticated, navigate, location.state]);

    // Get user's location
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    console.log('Geolocation error:', error);
                }
            );
        }
    }, []);

    // Fetch games
    const fetchGames = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                lat: userLocation.lat,
                lng: userLocation.lng,
                radius: searchRadius,
            };
            if (filterType !== 'all') {
                params.game_type = filterType;
            }
            // Add advanced filters
            if (advancedFilters.minPlayers) {
                params.min_players = parseInt(advancedFilters.minPlayers);
            }
            if (advancedFilters.maxPlayersLimit) {
                params.max_players_limit = parseInt(advancedFilters.maxPlayersLimit);
            }
            if (advancedFilters.hasOpenSlots) {
                params.has_open_slots = true;
            }
            if (advancedFilters.lookingForDm) {
                params.looking_for_dm = true;
            }
            if (advancedFilters.searchQuery) {
                params.search_query = advancedFilters.searchQuery;
            }
            if (advancedFilters.startDate) {
                params.start_date = advancedFilters.startDate.toISOString();
            }
            if (advancedFilters.endDate) {
                params.end_date = advancedFilters.endDate.toISOString();
            }
            const response = await gamesAPI.getGames(params);
            setGames(response.data);
        } catch (error) {
            console.error('Error fetching games:', error);
            toast.error('Failed to fetch games');
        } finally {
            setLoading(false);
        }
    }, [userLocation, filterType, searchRadius, advancedFilters]);

    useEffect(() => {
        if (isAuthenticated || location.state?.user) {
            fetchGames();
        }
    }, [fetchGames, isAuthenticated, location.state]);

    // WebSocket connection
    useEffect(() => {
        if (isAuthenticated || location.state?.user) {
            connect();
            
            // Initialize notifications
            notificationService.init();
            
            const unsubscribe = subscribe('dashboard', (message) => {
                if (message.type === 'game_created' || 
                    message.type === 'player_joined' || 
                    message.type === 'player_left' ||
                    message.type === 'game_cancelled') {
                    fetchGames();
                }
                
                // Show browser notification for player joined (if it's your game)
                if (message.type === 'player_joined' && message.game_id) {
                    const game = games.find(g => g.game_id === message.game_id);
                    if (game && game.host_id === currentUser?.user_id) {
                        notificationService.showPlayerJoined(game, message.user_name);
                    }
                }
                
                // Show browser notification for new messages
                if (message.type === 'new_message' && message.message?.user_id !== currentUser?.user_id) {
                    const game = games.find(g => g.game_id === message.game_id);
                    if (game) {
                        notificationService.showNewMessage(game, message.message?.user_name);
                    }
                }
            });

            return unsubscribe;
        }
    }, [connect, subscribe, fetchGames, isAuthenticated, location.state, games, currentUser]);

    // Host game handler
    const handleHostGame = async () => {
        if (!hostForm.latitude || !hostForm.longitude) {
            toast.error('Please select a location on the map or search for an address');
            return;
        }
        if (!hostForm.title || !hostForm.game_name) {
            toast.error('Please fill in game name and session title');
            return;
        }

        try {
            // Prepare form data with fallback address if not set
            const gameData = {
                ...hostForm,
                address: hostForm.address || `Location: ${hostForm.latitude.toFixed(4)}, ${hostForm.longitude.toFixed(4)}`,
            };
            await gamesAPI.createGame(gameData);
            toast.success('Game created successfully!');
            setShowHostModal(false);
            setHostForm({
                game_type: 'mtg',
                game_name: '',
                title: '',
                description: '',
                latitude: null,
                longitude: null,
                address: '',
                date_time: new Date(),
                duration_hours: 3,
                max_players: 4,
                looking_for_dm: false,
            });
            fetchGames();
        } catch (error) {
            console.error('Error creating game:', error);
            toast.error(error.response?.data?.detail || 'Failed to create game');
        }
    };

    // Join game handler
    const handleJoinGame = async (gameId) => {
        try {
            await gamesAPI.joinGame(gameId);
            toast.success('Joined game successfully!');
            // Refresh selected game
            const response = await gamesAPI.getGame(gameId);
            setSelectedGame(response.data);
            fetchGames();
        } catch (error) {
            console.error('Error joining game:', error);
            toast.error(error.response?.data?.detail || 'Failed to join game');
        }
    };

    // Leave game handler
    const handleLeaveGame = async (gameId) => {
        try {
            await gamesAPI.leaveGame(gameId);
            toast.success('Left game successfully');
            setShowGameModal(false);
            fetchGames();
        } catch (error) {
            console.error('Error leaving game:', error);
            toast.error(error.response?.data?.detail || 'Failed to leave game');
        }
    };

    // Handle address selection from autocomplete
    const handleAddressSelect = ({ address, lat, lng }) => {
        setHostForm({
            ...hostForm,
            address,
            latitude: lat,
            longitude: lng,
        });
    };

    // Count active filters
    const activeFilterCount = () => {
        let count = 0;
        if (advancedFilters.minPlayers) count++;
        if (advancedFilters.maxPlayersLimit) count++;
        if (advancedFilters.startDate) count++;
        if (advancedFilters.endDate) count++;
        if (advancedFilters.hasOpenSlots) count++;
        if (advancedFilters.lookingForDm) count++;
        if (advancedFilters.searchQuery) count++;
        return count;
    };

    // Clear all advanced filters
    const clearAdvancedFilters = () => {
        setAdvancedFilters({
            minPlayers: '',
            maxPlayersLimit: '',
            startDate: null,
            endDate: null,
            hasOpenSlots: false,
            lookingForDm: false,
            searchQuery: '',
        });
    };

    // Handle location picked from map
    const handleLocationPicked = async (loc) => {
        setHostForm({
            ...hostForm,
            latitude: loc.lat,
            longitude: loc.lng,
        });

        // Reverse geocode to get address
        try {
            const response = await geocodeAPI.reverseGeocode(loc.lat, loc.lng);
            if (response.data?.display_name) {
                setHostForm(prev => ({
                    ...prev,
                    address: response.data.display_name,
                }));
            }
        } catch (error) {
            console.error('Reverse geocoding error:', error);
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="matrix-loader" />
            </div>
        );
    }

    // If not authenticated and no user data passed from login, redirect to landing
    if (!currentUser) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="terminal-window p-8 text-center">
                    <div className="matrix-loader mx-auto mb-4" />
                    <p className="text-[#00ff41] font-mono">
                        {'>'} VERIFYING SESSION...
                    </p>
                    <p className="text-[#008f11] text-sm font-mono mt-2">
                        Please wait or <a href="/" className="underline hover:text-[#00ff41]">return to login</a>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="dashboard">
            <div className="scanlines" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-[1000] bg-[#050505]/95 border-b border-[#003b00] backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl sm:text-2xl font-bold text-[#00ff41] tracking-wider">
                            TABLETOP_LOOPER
                        </h1>
                        <div className="hidden sm:flex items-center gap-2 text-[#008f11] text-sm">
                            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-[#00ff41]' : 'bg-[#ff0033]'}`} />
                            <span>{connected ? 'ONLINE' : 'OFFLINE'}</span>
                        </div>
                    </div>

                    <nav className="flex items-center gap-2 sm:gap-4">
                        <Button
                            data-testid="mtg-tools-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/mtg')}
                            className="text-[#00ff41] hover:bg-[#00ff41]/10"
                        >
                            <Sparkles className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">MTG</span>
                        </Button>
                        <Button
                            data-testid="dnd-tools-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dnd')}
                            className="text-[#00ff41] hover:bg-[#00ff41]/10"
                        >
                            <Sword className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">D&D</span>
                        </Button>
                        <Button
                            data-testid="profile-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/profile')}
                            className="text-[#00ff41] hover:bg-[#00ff41]/10"
                        >
                            <User className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Profile</span>
                        </Button>
                        <Button
                            data-testid="history-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/history')}
                            className="text-[#00ff41] hover:bg-[#00ff41]/10"
                        >
                            <History className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">History</span>
                        </Button>
                        <Button
                            data-testid="calendar-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/calendar')}
                            className="text-[#00ff41] hover:bg-[#00ff41]/10"
                        >
                            <CalendarDays className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Calendar</span>
                        </Button>
                        <Button
                            data-testid="logout-btn"
                            variant="ghost"
                            size="sm"
                            onClick={logout}
                            className="text-[#ff0033] hover:bg-[#ff0033]/10"
                        >
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </nav>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-16 h-screen flex flex-col">
                {/* Controls Bar */}
                <div className="bg-[#0a0a0a] border-b border-[#003b00] p-4">
                    <div className="container mx-auto flex flex-wrap items-center gap-4">
                        {/* Search input */}
                        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#008f11]" />
                            <Input
                                data-testid="search-input"
                                value={advancedFilters.searchQuery}
                                onChange={(e) => setAdvancedFilters({...advancedFilters, searchQuery: e.target.value})}
                                placeholder="Search games..."
                                className="pl-10 bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger 
                                data-testid="filter-select"
                                className="w-[150px] bg-[#050505] border-[#003b00] text-[#00ff41]"
                            >
                                <SelectValue placeholder="Filter by type" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                <SelectItem value="all" className="text-[#00ff41]">All Games</SelectItem>
                                <SelectItem value="mtg" className="text-[#00ff41]">MTG</SelectItem>
                                <SelectItem value="dnd" className="text-[#00ff41]">D&D</SelectItem>
                                <SelectItem value="board_game" className="text-[#00ff41]">Board Games</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Radius slider */}
                        <div className="flex items-center gap-3 min-w-[200px]">
                            <Navigation className="w-4 h-4 text-[#008f11]" />
                            <Slider
                                data-testid="radius-slider"
                                value={[searchRadius]}
                                onValueChange={([val]) => setSearchRadius(val)}
                                min={5}
                                max={100}
                                step={5}
                                className="flex-1"
                            />
                            <span className="text-[#00ff41] text-sm font-mono w-16">
                                {searchRadius}mi
                            </span>
                        </div>

                        {/* Advanced Filters Toggle */}
                        <Button
                            data-testid="advanced-filters-btn"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                            className={`border-[#003b00] text-[#00ff41] hover:bg-[#00ff41]/10 ${activeFilterCount() > 0 ? 'bg-[#003b00]' : ''}`}
                        >
                            <SlidersHorizontal className="w-4 h-4 mr-2" />
                            Filters
                            {activeFilterCount() > 0 && (
                                <span className="ml-2 bg-[#00ff41] text-black text-xs px-1.5 py-0.5 rounded-full">
                                    {activeFilterCount()}
                                </span>
                            )}
                            {showFiltersPanel ? (
                                <ChevronUp className="w-4 h-4 ml-2" />
                            ) : (
                                <ChevronDown className="w-4 h-4 ml-2" />
                            )}
                        </Button>

                        <Button
                            data-testid="refresh-btn"
                            variant="outline"
                            size="sm"
                            onClick={fetchGames}
                            className="border-[#003b00] text-[#00ff41] hover:bg-[#00ff41]/10"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>

                        <Button
                            data-testid="host-game-btn"
                            onClick={() => setShowHostModal(true)}
                            className="bg-[#00ff41] text-black hover:bg-[#00ff41]/80 ml-auto"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Host Game
                        </Button>
                    </div>
                </div>

                {/* Main Content Area - Map and Filters Side Panel */}
                <div className="flex-1 flex relative overflow-hidden">
                    {/* Advanced Filters Side Panel */}
                    {showFiltersPanel && (
                        <motion.div
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: 280 }}
                            exit={{ opacity: 0, width: 0 }}
                            className="bg-[#0a0a0a] border-r border-[#003b00] p-4 overflow-y-auto flex-shrink-0"
                        >
                            <div className="space-y-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-[#00ff41] font-mono text-sm">ADVANCED_FILTERS</h3>
                                    <Button
                                        data-testid="clear-filters-btn"
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearAdvancedFilters}
                                        disabled={activeFilterCount() === 0}
                                        className="text-[#ff0033] hover:bg-[#ff0033]/10 h-6 px-2"
                                    >
                                        <X className="w-3 h-3 mr-1" />
                                        Clear
                                    </Button>
                                </div>

                                {/* Min Players */}
                                <div>
                                    <label className="text-xs text-[#008f11] mb-1 block">Min Players</label>
                                    <Input
                                        data-testid="min-players-input"
                                        type="number"
                                        value={advancedFilters.minPlayers}
                                        onChange={(e) => setAdvancedFilters({...advancedFilters, minPlayers: e.target.value})}
                                        placeholder="Any"
                                        min={0}
                                        className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                    />
                                </div>

                                {/* Max Players Limit */}
                                <div>
                                    <label className="text-xs text-[#008f11] mb-1 block">Max Players</label>
                                    <Input
                                        data-testid="max-players-input"
                                        type="number"
                                        value={advancedFilters.maxPlayersLimit}
                                        onChange={(e) => setAdvancedFilters({...advancedFilters, maxPlayersLimit: e.target.value})}
                                        placeholder="Any"
                                        min={1}
                                        className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                    />
                                </div>

                                {/* Start Date */}
                                <div>
                                    <label className="text-xs text-[#008f11] mb-1 block">From Date</label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                data-testid="start-date-btn"
                                                variant="outline"
                                                className="w-full justify-start text-left bg-[#050505] border-[#003b00] text-[#00ff41]"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {advancedFilters.startDate ? format(advancedFilters.startDate, 'MMM d') : 'Any'}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-[#0a0a0a] border-[#003b00]" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={advancedFilters.startDate}
                                                onSelect={(date) => setAdvancedFilters({...advancedFilters, startDate: date})}
                                                className="text-[#00ff41]"
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                {/* End Date */}
                                <div>
                                    <label className="text-xs text-[#008f11] mb-1 block">To Date</label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                data-testid="end-date-btn"
                                                variant="outline"
                                                className="w-full justify-start text-left bg-[#050505] border-[#003b00] text-[#00ff41]"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {advancedFilters.endDate ? format(advancedFilters.endDate, 'MMM d') : 'Any'}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-[#0a0a0a] border-[#003b00]" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={advancedFilters.endDate}
                                                onSelect={(date) => setAdvancedFilters({...advancedFilters, endDate: date})}
                                                className="text-[#00ff41]"
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                {/* Checkboxes */}
                                <div className="space-y-3 pt-2 border-t border-[#003b00]">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            data-testid="has-open-slots-checkbox"
                                            type="checkbox"
                                            checked={advancedFilters.hasOpenSlots}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, hasOpenSlots: e.target.checked})}
                                            className="accent-[#00ff41] w-4 h-4"
                                        />
                                        <span className="text-sm text-[#00ff41]">Open Slots Only</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            data-testid="looking-for-dm-checkbox"
                                            type="checkbox"
                                            checked={advancedFilters.lookingForDm}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, lookingForDm: e.target.checked})}
                                            className="accent-[#00ff41] w-4 h-4"
                                        />
                                        <span className="text-sm text-[#00ff41]">Needs DM</span>
                                    </label>
                                </div>

                                {/* Apply button */}
                                <Button
                                    data-testid="apply-filters-btn"
                                    onClick={fetchGames}
                                    className="w-full bg-[#00ff41] text-black hover:bg-[#00ff41]/80 mt-4"
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {/* Map */}
                    <div className="flex-1 relative">
                        <MapContainer
                            center={[userLocation.lat, userLocation.lng]}
                            zoom={12}
                            className="h-full w-full"
                            style={{ background: '#050505' }}
                        >
                            <TileLayer
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            />
                            <MapCenterUpdater center={[userLocation.lat, userLocation.lng]} />
                        
                        {/* User location marker */}
                        <Marker position={[userLocation.lat, userLocation.lng]}>
                            <Popup>
                                <div className="text-[#00ff41] font-mono">
                                    <strong>Your Location</strong>
                                </div>
                            </Popup>
                        </Marker>

                        {/* Game markers */}
                        {games.map((game) => (
                            <Marker
                                key={game.game_id}
                                position={[game.latitude, game.longitude]}
                                icon={gameIcons[game.game_type] || gameIcons.board_game}
                                eventHandlers={{
                                    click: () => {
                                        setSelectedGame(game);
                                        setShowGameModal(true);
                                    },
                                }}
                            >
                                <Popup>
                                    <div className="font-mono">
                                        <strong className="text-[#00ff41]">{game.title}</strong>
                                        <p className="text-sm">{game.game_name}</p>
                                        {game.distance_miles && (
                                            <p className="text-xs text-[#008f11]">{game.distance_miles} mi away</p>
                                        )}
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>

                    {/* Games count overlay */}
                    <div className="absolute bottom-4 left-4 z-[500] bg-[#050505]/90 border border-[#003b00] px-4 py-2">
                        <span className="text-[#00ff41] font-mono text-sm">
                            {games.length} GAMES FOUND (within {searchRadius}mi)
                        </span>
                    </div>
                </div>

                {/* Games List Panel */}
                <div className="w-80 bg-[#0a0a0a] border-l border-[#003b00] overflow-y-auto flex-shrink-0">
                    <div className="p-4 border-b border-[#003b00] sticky top-0 bg-[#0a0a0a] z-10">
                        <h3 className="text-[#00ff41] font-mono text-sm">NEARBY_GAMES ({games.length})</h3>
                    </div>
                    <div className="p-2 space-y-2">
                        {loading ? (
                            <div className="flex justify-center py-10">
                                <div className="matrix-loader" />
                            </div>
                        ) : games.length > 0 ? (
                            games.map((game) => (
                                <div
                                    key={game.game_id}
                                    data-testid={`game-card-${game.game_id}`}
                                    onClick={() => {
                                        setSelectedGame(game);
                                        setShowGameModal(true);
                                    }}
                                    className="p-3 bg-[#050505] border border-[#003b00] cursor-pointer hover:border-[#00ff41] transition-colors"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            {game.game_type === 'mtg' && <Sparkles className="w-4 h-4 text-[#00ff41]" />}
                                            {game.game_type === 'dnd' && <Sword className="w-4 h-4 text-[#ff6b6b]" />}
                                            {game.game_type === 'board_game' && <Store className="w-4 h-4 text-[#4ecdc4]" />}
                                            <span className="text-xs text-[#008f11] uppercase">{game.game_type}</span>
                                        </div>
                                        {game.distance_miles && (
                                            <span className="text-xs text-[#008f11]">{game.distance_miles}mi</span>
                                        )}
                                    </div>
                                    <h4 className="text-[#00ff41] font-bold text-sm mb-1 truncate">{game.title}</h4>
                                    <p className="text-[#008f11] text-xs mb-2">{game.game_name}</p>
                                    <div className="flex items-center justify-between text-xs text-[#008f11]">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {format(new Date(game.date_time), 'MMM d, h:mm a')}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {game.current_players?.length || 0}/{game.max_players}
                                        </span>
                                    </div>
                                    {game.looking_for_dm && (
                                        <div className="mt-2 text-xs text-[#ff0033] flex items-center gap-1">
                                            <Sword className="w-3 h-3" />
                                            Needs DM
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-10">
                                <MapPin className="w-8 h-8 text-[#003b00] mx-auto mb-2" />
                                <p className="text-[#008f11] text-sm">No games found in this area</p>
                                <p className="text-[#008f11] text-xs mt-1">Try expanding your search radius</p>
                            </div>
                        )}
                    </div>
                </div>
                </div>
            </main>

            {/* Host Game Modal */}
            <Dialog open={showHostModal} onOpenChange={setShowHostModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[85vh] overflow-y-auto z-[9999]">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider">HOST_NEW_GAME</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Create a new game session for others to join
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Game Type</label>
                                <Select 
                                    value={hostForm.game_type} 
                                    onValueChange={(v) => setHostForm({...hostForm, game_type: v})}
                                >
                                    <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                        <SelectItem value="mtg">Magic: The Gathering</SelectItem>
                                        <SelectItem value="dnd">Dungeons & Dragons</SelectItem>
                                        <SelectItem value="board_game">Board Game</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Game Name</label>
                                <Input
                                    data-testid="game-name-input"
                                    value={hostForm.game_name}
                                    onChange={(e) => setHostForm({...hostForm, game_name: e.target.value})}
                                    placeholder="e.g., Commander, Curse of Strahd"
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Session Title</label>
                            <Input
                                data-testid="session-title-input"
                                value={hostForm.title}
                                onChange={(e) => setHostForm({...hostForm, title: e.target.value})}
                                placeholder="e.g., Friday Night Commander"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Description</label>
                            <textarea
                                data-testid="description-input"
                                value={hostForm.description}
                                onChange={(e) => setHostForm({...hostForm, description: e.target.value})}
                                placeholder="Tell players what to expect..."
                                className="w-full h-20 bg-[#050505] border border-[#003b00] text-[#00ff41] p-2 resize-none rounded-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Date</label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start text-left bg-[#050505] border-[#003b00] text-[#00ff41]"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {format(hostForm.date_time, 'PPP')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 bg-[#0a0a0a] border-[#003b00]">
                                        <Calendar
                                            mode="single"
                                            selected={hostForm.date_time}
                                            onSelect={(date) => date && setHostForm({...hostForm, date_time: date})}
                                            className="text-[#00ff41]"
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Time</label>
                                <Input
                                    type="time"
                                    value={format(hostForm.date_time, 'HH:mm')}
                                    onChange={(e) => {
                                        const [hours, minutes] = e.target.value.split(':');
                                        const newDate = new Date(hostForm.date_time);
                                        newDate.setHours(parseInt(hours), parseInt(minutes));
                                        setHostForm({...hostForm, date_time: newDate});
                                    }}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Duration (hours)</label>
                                <Input
                                    type="number"
                                    value={hostForm.duration_hours}
                                    onChange={(e) => setHostForm({...hostForm, duration_hours: parseFloat(e.target.value)})}
                                    min={1}
                                    max={12}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>

                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Max Players</label>
                                <Input
                                    type="number"
                                    value={hostForm.max_players}
                                    onChange={(e) => setHostForm({...hostForm, max_players: parseInt(e.target.value)})}
                                    min={2}
                                    max={20}
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Location (type to search)</label>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <AddressAutocomplete
                                        value={hostForm.address}
                                        onChange={(val) => setHostForm({...hostForm, address: val})}
                                        onSelect={handleAddressSelect}
                                        placeholder="Search for an address..."
                                    />
                                </div>
                                <Button
                                    data-testid="pick-location-btn"
                                    variant="outline"
                                    onClick={() => setPickingLocation(true)}
                                    className="border-[#003b00] text-[#00ff41]"
                                >
                                    <MapPin className="w-4 h-4" />
                                </Button>
                            </div>
                            {hostForm.latitude && (
                                <p className="text-xs text-[#008f11] mt-1">
                                    Coordinates: {hostForm.latitude.toFixed(4)}, {hostForm.longitude.toFixed(4)}
                                </p>
                            )}
                        </div>

                        {hostForm.game_type === 'dnd' && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={hostForm.looking_for_dm}
                                    onChange={(e) => setHostForm({...hostForm, looking_for_dm: e.target.checked})}
                                    className="accent-[#00ff41]"
                                />
                                <label className="text-sm text-[#008f11]">Looking for a DM</label>
                            </div>
                        )}

                        <Button
                            data-testid="create-game-btn"
                            onClick={handleHostGame}
                            className="w-full bg-[#00ff41] text-black hover:bg-[#00ff41]/80"
                        >
                            CREATE_GAME
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Location Picker Modal */}
            <Dialog open={pickingLocation} onOpenChange={setPickingLocation}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>SELECT_LOCATION</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Click on the map to select your game location
                        </DialogDescription>
                    </DialogHeader>
                    <div className="h-[400px] relative">
                        <MapContainer
                            center={[userLocation.lat, userLocation.lng]}
                            zoom={13}
                            className="h-full w-full"
                        >
                            <TileLayer
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                attribution='&copy; OpenStreetMap'
                            />
                            <LocationPicker
                                onLocationSelect={handleLocationPicked}
                                selectedLocation={
                                    hostForm.latitude 
                                        ? { lat: hostForm.latitude, lng: hostForm.longitude }
                                        : null
                                }
                            />
                        </MapContainer>
                    </div>
                    <Button
                        onClick={() => setPickingLocation(false)}
                        className="bg-[#00ff41] text-black"
                    >
                        CONFIRM_LOCATION
                    </Button>
                </DialogContent>
            </Dialog>

            {/* Game Details Modal with Chat */}
            <Dialog open={showGameModal} onOpenChange={setShowGameModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider">
                            {selectedGame?.title}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedGame && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="data-row">
                                    <p className="data-label">Game Type</p>
                                    <p className="data-value">{selectedGame.game_type.toUpperCase()}</p>
                                </div>
                                <div className="data-row">
                                    <p className="data-label">Game</p>
                                    <p className="data-value">{selectedGame.game_name}</p>
                                </div>
                            </div>

                            <div className="data-row">
                                <p className="data-label">Host</p>
                                <p className="data-value">{selectedGame.host_name}</p>
                            </div>

                            <div className="data-row">
                                <p className="data-label">Date & Time</p>
                                <p className="data-value">
                                    {format(new Date(selectedGame.date_time), 'PPP p')}
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="data-row">
                                    <p className="data-label">Duration</p>
                                    <p className="data-value">{selectedGame.duration_hours}h</p>
                                </div>
                                <div className="data-row">
                                    <p className="data-label">Players</p>
                                    <p className="data-value">
                                        {selectedGame.current_players?.length || 0}/{selectedGame.max_players}
                                    </p>
                                </div>
                                {selectedGame.distance_miles && (
                                    <div className="data-row">
                                        <p className="data-label">Distance</p>
                                        <p className="data-value">{selectedGame.distance_miles} mi</p>
                                    </div>
                                )}
                            </div>

                            <div className="data-row">
                                <p className="data-label">Location</p>
                                <p className="data-value text-sm">{selectedGame.address || 'Location on map'}</p>
                            </div>

                            {selectedGame.description && (
                                <div className="data-row">
                                    <p className="data-label">Description</p>
                                    <p className="data-value text-sm">{selectedGame.description}</p>
                                </div>
                            )}

                            {selectedGame.looking_for_dm && (
                                <div className="bg-[#2d132c] border border-[#ff0033] p-2 text-center">
                                    <span className="text-[#ff0033]">LOOKING FOR DM</span>
                                </div>
                            )}

                            {/* Game Chat */}
                            <GameChat
                                gameId={selectedGame.game_id}
                                currentUser={currentUser}
                                isParticipant={selectedGame.current_players?.includes(currentUser?.user_id)}
                            />

                            <div className="flex gap-2 pt-4">
                                {selectedGame.current_players?.includes(currentUser?.user_id) ? (
                                    selectedGame.host_id === currentUser?.user_id ? (
                                        <Button
                                            data-testid="cancel-game-btn"
                                            variant="destructive"
                                            className="flex-1"
                                            onClick={() => {
                                                gamesAPI.cancelGame(selectedGame.game_id);
                                                setShowGameModal(false);
                                                fetchGames();
                                            }}
                                        >
                                            CANCEL_GAME
                                        </Button>
                                    ) : (
                                        <Button
                                            data-testid="leave-game-btn"
                                            variant="outline"
                                            className="flex-1 border-[#ff0033] text-[#ff0033]"
                                            onClick={() => handleLeaveGame(selectedGame.game_id)}
                                        >
                                            LEAVE_GAME
                                        </Button>
                                    )
                                ) : (
                                    <Button
                                        data-testid="join-game-btn"
                                        className="flex-1 bg-[#00ff41] text-black"
                                        onClick={() => handleJoinGame(selectedGame.game_id)}
                                        disabled={selectedGame.current_players?.length >= selectedGame.max_players}
                                    >
                                        {selectedGame.current_players?.length >= selectedGame.max_players 
                                            ? 'GAME_FULL' 
                                            : 'JOIN_GAME'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Dashboard;
